(function (root, factory) {
    const api = factory();
    if (typeof module === "object" && module.exports) {
        module.exports = api;
    } else {
        root.LogInventory = api;
    }
}(typeof globalThis !== "undefined" ? globalThis : this, function () {
    "use strict";

    const CSV_NAME = "workflow-logging-inventory-full.csv";
    const HEADERS = ["File", "Line", "Level", "Message", "DataAppended", "PIIRisk", "PIIRiskSource"];
    const LEVEL_NAMES = ["Error", "Warn", "Info", "Debug"];
    const LEVEL_ALIASES = {
        error: "Error", exception: "Error", warn: "Warn", warning: "Warn", info: "Info",
        information: "Info", notice: "Info", debug: "Debug", fine: "Debug",
        finer: "Debug", finest: "Debug", trace: "Debug",
    };
    const SOURCE_EXTENSIONS = new Set([
        ".c", ".cc", ".cpp", ".cs", ".cxx", ".go", ".groovy", ".h", ".hpp",
        ".java", ".js", ".jsx", ".kt", ".kts", ".m", ".mm", ".php", ".py",
        ".rb", ".rs", ".scala", ".sh", ".swift", ".ts", ".tsx", ".vue",
    ]);
    const IGNORED_DIRECTORIES = new Set([
        ".git", ".gradle", ".idea", ".mypy_cache", ".pytest_cache", ".tox",
        ".venv", "bin", "build", "coverage", "dist", "node_modules", "out",
        "target", "test", "tests", "__tests__", "vendor", "venv",
    ]);
    const LOG_PATTERNS = [
        {
            expression: /\b(?:[A-Za-z_$][\w$]*(?:log|logger)|log|logger|logging|console)\s*\.\s*(error|exception|warn(?:ing)?|notice|info(?:rmation)?|debug|fine(?:r|st)?|trace)\s*\(/gi,
            generic: false,
        },
        {
            expression: /\b[A-Za-z_$][\w$]*(?:log|logger)\s*\.\s*Log(Error|Warning|Information|Debug|Trace)\s*\(/gi,
            generic: false,
        },
        {
            expression: /\b(?:[A-Za-z_$][\w$]*(?:log|logger)|log|logger|logging)\s*\.\s*log\s*\(\s*(?:Level\.|logging\.)?(ERROR|WARNING|WARN|INFO|DEBUG|TRACE)\s*,/gi,
            generic: true,
        },
    ];
    const PII_CATEGORIES = [
        [/\be-?mail\b|email\s*address|emailaddress|mail\s*id/i, "email"],
        [/\bfirst\s*name\b/i, "first name"],
        [/\blast\s*name\b/i, "last name"],
        [/\bfull\s*name\b/i, "full name"],
        [/\busername\b|\buser\s*name\b/i, "username"],
        [/\bphone\b/i, "phone"],
        [/\bmobile\b/i, "mobile"],
        [/\bpostal\b/i, "postal address"],
        [/\bssn\b/i, "SSN"],
        [/\bpassport\b/i, "passport"],
        [/date\s*of\s*birth|\bdob\b/i, "date of birth"],
        [/credit\s*card/i, "credit card"],
        [/\bbrid\b/i, "BRID"],
        [/x-forwarded-for/i, "X-Forwarded-For header"],
        [/ip\s*address|ipaddress|requestip/i, "IP address"],
        [/user\s*agent|useragent/i, "user agent"],
        [/\bpasscode\b/i, "passcode"],
        [/\bpassword\b/i, "password"],
        [/\bfrom\s*=.*\bto\s*=|\bsubject\s*=/i, "mail From/To/Subject"],
        [/\bcfusi\b/i, "CFUSI cookie"],
    ];

    function shouldIncludeFile(filePath) {
        const normalized = filePath.replaceAll("\\", "/");
        const parts = normalized.split("/");
        if (parts.slice(0, -1).some((part) => IGNORED_DIRECTORIES.has(part))) return false;
        const dot = normalized.lastIndexOf(".");
        return dot >= 0 && SOURCE_EXTENSIONS.has(normalized.slice(dot).toLowerCase());
    }

    function maskCode(content) {
        const masked = content.split("");
        let index = 0;
        let state = "code";
        let delimiter = null;
        while (index < content.length) {
            if (state === "code") {
                if (content.startsWith("//", index) || content[index] === "#") {
                    state = "lineComment";
                } else if (content.startsWith("/*", index)) {
                    state = "blockComment";
                    masked[index] = masked[index + 1] = " ";
                    index += 2;
                    continue;
                } else if (content.startsWith('"""', index) || content.startsWith("'''", index)) {
                    state = "string";
                    delimiter = content.slice(index, index + 3);
                    masked[index] = masked[index + 1] = masked[index + 2] = " ";
                    index += 3;
                    continue;
                } else if (["\"", "'", "`"].includes(content[index])) {
                    state = "string";
                    delimiter = content[index];
                } else {
                    index += 1;
                    continue;
                }
            } else if (state === "lineComment" && content[index] === "\n") {
                state = "code";
                index += 1;
                continue;
            } else if (state === "blockComment" && content.startsWith("*/", index)) {
                masked[index] = masked[index + 1] = " ";
                index += 2;
                state = "code";
                continue;
            } else if (state === "string") {
                if (content[index] === "\\") {
                    masked[index] = " ";
                    if (index + 1 < content.length && content[index + 1] !== "\n") {
                        masked[index + 1] = " ";
                        index += 2;
                        continue;
                    }
                } else if (content.startsWith(delimiter, index)) {
                    for (let offset = 0; offset < delimiter.length; offset += 1) masked[index + offset] = " ";
                    index += delimiter.length;
                    state = "code";
                    delimiter = null;
                    continue;
                }
            }
            if (content[index] !== "\n" && content[index] !== "\r") masked[index] = " ";
            index += 1;
        }
        return masked.join("");
    }

    function findClosingParenthesis(content, openingIndex) {
        let depth = 0;
        let quote = null;
        let escaped = false;
        for (let index = openingIndex; index < content.length; index += 1) {
            const character = content[index];
            if (escaped) {
                escaped = false;
            } else if (character === "\\" && quote) {
                escaped = true;
            } else if (quote) {
                if (character === quote) quote = null;
            } else if (["\"", "'", "`"].includes(character)) {
                quote = character;
            } else if (character === "(") {
                depth += 1;
            } else if (character === ")") {
                depth -= 1;
                if (depth === 0) return index;
            }
        }
        return null;
    }

    function firstArgument(argumentsText) {
        let depth = 0;
        let quote = null;
        let escaped = false;
        for (let index = 0; index < argumentsText.length; index += 1) {
            const character = argumentsText[index];
            if (escaped) {
                escaped = false;
            } else if (character === "\\" && quote) {
                escaped = true;
            } else if (quote) {
                if (character === quote) quote = null;
            } else if (["\"", "'", "`"].includes(character)) {
                quote = character;
            } else if ("([{".includes(character)) {
                depth += 1;
            } else if (")]}".includes(character)) {
                depth = Math.max(0, depth - 1);
            } else if (character === "," && depth === 0) {
                return argumentsText.slice(0, index).trim();
            }
        }
        return argumentsText.trim();
    }

    function decodeQuotedString(expression) {
        const quote = expression[0];
        if (!["\"", "'"].includes(quote) || !expression.endsWith(quote)) return null;
        const body = expression.slice(1, -1);
        return body.replace(/\\(u[0-9a-fA-F]{4}|x[0-9a-fA-F]{2}|n|r|t|b|f|v|0|\\|"|')/g, (match, escape) => {
            if (escape.startsWith("u")) return String.fromCharCode(parseInt(escape.slice(1), 16));
            if (escape.startsWith("x")) return String.fromCharCode(parseInt(escape.slice(1), 16));
            return { n: "\n", r: "\r", t: "\t", b: "\b", f: "\f", v: "\v", 0: "\0", "\\": "\\", "\"": "\"", "'": "'" }[escape];
        });
    }

    function describeLogMessage(argumentsText) {
        const expression = firstArgument(argumentsText).replace(/\s+/g, " ").trim();
        if (!expression) return "";
        if (/^(?:[A-Za-z_$][\w$]*\.)?getMessage\(\)$/.test(expression)) {
            return "(logs caught exception message via e.getMessage())";
        }
        if (expression.startsWith("`") && expression.endsWith("`")) {
            return expression.slice(1, -1).replace(/\$\{[^}]+\}/g, "{}");
        }
        return decodeQuotedString(expression) ?? expression;
    }

    function packageName(content, relativePath) {
        const declaration = /^\s*(?:package|namespace)\s+([A-Za-z_][\w.]*)\s*[;{]?/m.exec(content);
        if (declaration) return declaration[1];
        const slash = relativePath.lastIndexOf("/");
        return slash < 0 ? "(root)" : relativePath.slice(0, slash).replaceAll("/", ".");
    }

    function formatList(items) {
        return `[${items.map((item) => `"${item}"`).join(", ")}]`;
    }

    function classifyPii(message) {
        const reasons = PII_CATEGORIES.filter(([pattern]) => pattern.test(message || "")).map(([, reason]) => reason);
        if ((message || "").includes(".toString(") && !reasons.includes("toString call")) reasons.push("toString call");
        return reasons;
    }

    function getDataAppended(message) {
        const labels = [];
        let searchStart = 0;
        for (const match of (message || "").matchAll(/\{\}|%[sd]/g)) {
            const segment = message.slice(searchStart, match.index);
            const label = /([A-Za-z_][A-Za-z0-9_]*)\s*[:=]\s*$/.exec(segment);
            if (label) labels.push(label[1]);
            searchStart = match.index + match[0].length;
        }
        return formatList(labels);
    }

    function scanFiles(files) {
        const rows = [];
        // Package names come from untrusted source paths/declarations, so avoid plain-object
        // prototype-pollution keys like "__proto__" or "constructor".
        const summary = Object.create(null);
        for (const file of files) {
            const relativePath = file.path.replaceAll("\\", "/");
            if (!shouldIncludeFile(relativePath)) continue;
            const content = file.content;
            const packageValue = packageName(content, relativePath);
            const masked = maskCode(content);
            const matches = [];
            for (const pattern of LOG_PATTERNS) {
                pattern.expression.lastIndex = 0;
                for (const match of masked.matchAll(pattern.expression)) matches.push({ match, generic: pattern.generic });
            }
            matches.sort((left, right) => left.match.index - right.match.index);
            const consumedOffsets = new Set();
            for (const { match, generic } of matches) {
                const openingIndex = content.indexOf("(", match.index);
                if (openingIndex < 0 || consumedOffsets.has(openingIndex)) continue;
                // Scan the comment/string-masked text so a stray ")" inside a comment
                // (e.g. logger.info("x" /* ) */);) can't be mistaken for the call's closer.
                const closingIndex = findClosingParenthesis(masked, openingIndex);
                if (closingIndex === null) continue;
                consumedOffsets.add(openingIndex);
                const argumentStart = generic ? match.index + match[0].length : openingIndex + 1;
                const argumentsText = content.slice(argumentStart, closingIndex);
                const message = describeLogMessage(argumentsText);
                const level = LEVEL_ALIASES[match[1].toLowerCase()];
                // Classify against all logging arguments, not just the displayed message.
                const reasons = classifyPii(argumentsText);
                rows.push({
                    File: relativePath,
                    Line: String(content.slice(0, match.index).split("\n").length),
                    Level: level,
                    Message: message,
                    DataAppended: getDataAppended(message),
                    PIIRisk: reasons.length ? "true" : "false",
                    PIIRiskSource: reasons.length ? formatList(reasons) : "n/a",
                    Package: packageValue,
                });
                if (!summary[packageValue]) summary[packageValue] = {};
                summary[packageValue][level] = (summary[packageValue][level] || 0) + 1;
                summary[packageValue].piiRiskMessages = (summary[packageValue].piiRiskMessages || 0) + (reasons.length ? 1 : 0);
            }
        }
        rows.sort((left, right) => left.File.toLowerCase().localeCompare(right.File.toLowerCase()) || Number(left.Line) - Number(right.Line));
        return { rows, summary };
    }

    function summarizeRows(rows) {
        // Same prototype-pollution protection as scanFiles: row.Package is untrusted.
        const summary = Object.create(null);
        for (const row of rows) {
            if (!summary[row.Package]) summary[row.Package] = {};
            summary[row.Package][row.Level] = (summary[row.Package][row.Level] || 0) + 1;
            summary[row.Package].piiRiskMessages = (summary[row.Package].piiRiskMessages || 0) + (row.PIIRisk === "true" ? 1 : 0);
        }
        return summary;
    }

    function csvCell(value) {
        // Prefix formula-leading characters so spreadsheet apps don't execute them (CSV injection).
        const text = String(value ?? "").replace(/^[=+\-@]/, "'$&");
        return `"${text.replaceAll("\"", "\"\"")}"`;
    }

    function createCsv(rows) {
        return `${[HEADERS, ...rows.map((row) => HEADERS.map((header) => row[header]))]
            .map((values) => values.map(csvCell).join(","))
            .join("\r\n")}\r\n`;
    }

    return { CSV_NAME, HEADERS, LEVEL_NAMES, createCsv, scanFiles, shouldIncludeFile, summarizeRows };
}));